import React from 'react';
import { Eye } from 'lucide-react';
import { EventInfoCard } from '@/components/ui/EventInfoCard';
import { TransferBookingHeader } from './TransferBookingHeader';
import { TransferAddOnsDisplay } from './TransferAddOnsDisplay';
import { TierRibbon } from './TierRibbon';
import type { BookingCategoryClass } from '@/features/booking/types/bookingTypes';

interface AddOnItem {
  add_on?: {
    id?: string;
    name?: string;
    image_id?: string | null;
    price?: number;
  };
  quantity?: number;
  price?: number;
}

interface TransferBookingCardProps {
  /** Event data */
  event?: {
    id?: string;
    name?: string;
    description?: string | null;
    card_image_id?: string | null;
    image_id?: string | null;
    dress_code?: string | null;
  } | null;
  /** Event schedule data */
  eventSchedule?: {
    start_time?: string | null;
    duration?: number | null;
    event_id?: string;
  } | null;
  /** Booking category data */
  bookingCategory?: {
    name?: string;
    card_image_id?: string | null;
    is_table_booking?: boolean;
    class?: BookingCategoryClass;
  } | null;
  /** Start time for the booking */
  startTime?: string | null;
  /** Add-ons for the booking */
  addons: AddOnItem[];
  /** Table booking details */
  tableDetails?: {
    tableNumber?: string | null;
    tableSize?: number | null;
    bookingTime?: string | null;
    slotDuration?: number | null;
  };
  /** Business timezone */
  businessTimeZone?: string;
  /** Currency code for pricing display */
  currency?: string;
  /** Handler for View Event button click */
  onViewEvent?: () => void;
  /** Handler for category image card click */
  onCategoryClick?: () => void;
  /** Handler for add-on card click */
  onAddonClick?: (addon: AddOnItem) => void;
}

/**
 * Composite booking card component for transfer page.
 * Combines EventInfoCard, TransferBookingHeader, and TransferAddOnsDisplay.
 */
export const TransferBookingCard: React.FC<TransferBookingCardProps> = ({
  event,
  eventSchedule,
  bookingCategory,
  startTime,
  addons,
  tableDetails,
  businessTimeZone,
  currency,
  onViewEvent,
  onCategoryClick,
  onAddonClick,
}) => {
  // View Event button
  const viewEventButton = onViewEvent ? (
    <button
      onClick={onViewEvent}
      className="px-3 py-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-medium rounded-lg border border-gray-600 hover:border-gray-500 transition flex items-center gap-2 text-sm"
    >
      <Eye className="w-4 h-4" /> View Event
    </button>
  ) : undefined;

  // Header content
  const headerContent = (
    <TransferBookingHeader
      eventName={event?.name || 'Event'}
      eventDescription={event?.description}
      startTime={startTime}
      dressCode={event?.dress_code}
      eventDuration={eventSchedule?.duration}
      categoryName={bookingCategory?.name}
      categoryImageId={bookingCategory?.card_image_id}
      isTableBooking={bookingCategory?.is_table_booking}
      tableDetails={tableDetails}
      businessTimeZone={businessTimeZone}
      onCategoryClick={onCategoryClick}
    />
  );

  return (
    <EventInfoCard
      eventImageId={event?.card_image_id || event?.image_id}
      topRightAction={viewEventButton}
    >
      {/* Header Section */}
      <div
        className="p-6 border-b border-gray-700/30"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        {headerContent}
      </div>

      {/* Tier Ribbon Separator - flush between header and content */}
      <TierRibbon
        tierClass={bookingCategory?.class || 'Standard'}
        variant="separator"
        textSuffix="TIER"
      />

      {/* Content Section */}
      {addons.length > 0 && (
        <div className="p-6 bg-black/40">
          <TransferAddOnsDisplay
            addons={addons}
            onAddonClick={onAddonClick}
            currency={currency}
          />
        </div>
      )}
    </EventInfoCard>
  );
};

export default TransferBookingCard;
